import api from './axiosConfig';

/**
 * Get all payments with pagination and filters
 * @param {Object} params - Query parameters (page, limit, status, etc.)
 * @returns {Promise<Object>} - Payments data and pagination info
 */
export const getPayments = async (params = {}) => {
  try {
    const response = await api.get('/payments', { 
      params,
      authenticated: true 
    });
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || 'Failed to get payments');
    }
    throw new Error('Network error. Please try again.');
  }
};

/**
 * Get payment by ID
 * @param {string} id - Payment ID
 * @returns {Promise<Object>} - Payment data
 */
export const getPaymentById = async (id) => {
  try {
    const response = await api.get(`/payments/${id}`, { authenticated: true });
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      throw new Error('Payment not found');
    }
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || 'Failed to get payment');
    }
    throw new Error('Network error. Please try again.');
  }
};

/**
 * Create new payment
 * @param {Object} paymentData - Payment data
 * @returns {Promise<Object>} - Created payment data
 */
export const createPayment = async (paymentData) => {
  try {
    const response = await api.post('/payments', paymentData, { authenticated: true });
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || 'Failed to create payment');
    }
    throw new Error('Network error. Please try again.');
  }
};

/**
 * Update payment status
 * @param {string} id - Payment ID
 * @param {Object} updateData - Updated payment data (status, notes)
 * @returns {Promise<Object>} - Updated payment data
 */
export const updatePayment = async (id, updateData) => {
  try {
    const response = await api.put(`/payments/${id}`, updateData, { authenticated: true });
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      throw new Error('Payment not found');
    }
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || 'Failed to update payment');
    }
    throw new Error('Network error. Please try again.');
  }
};

/**
 * Get payment statistics
 * @returns {Promise<Object>} - Payment statistics
 */
export const getPaymentStats = async () => {
  try {
    const response = await api.get('/payments/stats', { authenticated: true });
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || 'Failed to get payment statistics');
    }
    throw new Error('Network error. Please try again.');
  }
};

/**
 * Submit checkout (combined customer and payment creation)
 * @param {Object} checkoutData - Combined customer and payment data
 * @returns {Promise<Object>} - Checkout result with reference number
 */
export const submitCheckout = async (checkoutData) => {
  try {
    const response = await api.post('/payments/checkout', checkoutData);
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || 'Failed to process checkout');
    }
    throw new Error('Network error. Please try again.');
  }
};

/**
 * Export payments to CSV
 * @param {Object} params - Query parameters for filtering (status, paymentMethod, startDate, endDate)
 * @returns {Promise<Blob>} - CSV file as a blob
 */
export const exportPayments = async (params = {}) => {
  try {
    const response = await api.get('/payments/export', { 
      params,
      responseType: 'blob',
      authenticated: true 
    });
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      // Handle blob error
      const text = await new Response(error.response.data).text();
      let message;
      try {
        const json = JSON.parse(text);
        message = json.message || 'Failed to export payments';
      } catch (e) {
        message = 'Failed to export payments';
      }
      throw new Error(message);
    }
    throw new Error('Network error. Please try again.');
  }
};